<template>
  <div v-if="representative" class="flex flex-col h-full overflow-hidden">
    <!-- Header -->
    <BaseDetailsPreviewEditHeader
      :is-horizontal-layout="isHorizontalLayout"
      :title="modifiedRepresentative?.trader?.name ? modifiedRepresentative.trader.name : '-'"
      link-name="representatives"
      :is-modified="isModified"
      :is-edit-active="representative.isFromManager && !representative.deleted"
      :mode="mode"
      @edit-mode="onEditMode"
      @cancel="onCancelEdit"
      @save="onSaveChanges"
      @horizontal-layout="(value: boolean) => (isHorizontalLayout = value)" />

    <div v-if="isHorizontalLayout">
      <nav
        role="section-nav"
        class="flex justify-between px-6 text-sm font-medium text-center text-gray-500 sm:px-0 min-h-9">
        <ul class="flex flex-wrap items-center gap-4 pl-4 -mb-px">
          <li
            v-for="tab in section1Tabs"
            :key="tab.name"
            @click="selectedTab = tab.name"
            class="py-1.5 cursor-pointer"
            :class="[
              selectedTab === tab.name ? 'border-b-2 border-theme-500' : '',
              tab.validator && $v[tab.validator].required.$invalid
                ? 'text-red-500 !border-red-500'
                : '',
            ]">
            <p>
              {{ tab.label }}
            </p>
          </li>
        </ul>
      </nav>
    </div>
    <div class="flex-1 overflow-hidden">
      <!-- Horizontal Layout -->
      <div v-if="isHorizontalLayout" class="h-full">
        <PerfectScrollbar class="w-full h-full py-4 space-y-2" ref="scrollbarId">
          <KeepAlive>
            <component
              :is="currentComponent"
              v-model="modifiedRepresentative"
              v-model:measurement-units="measurementUnits"
              v-model:isMeasurementUnitModified="isMeasurementUnitModified"
              v-model:payment-types="paymentTypes"
              v-model:is-payment-type-modified="isPaymentTypeModified"
              v-model:material-alter-codes="materialAlterCodes"
              v-model:is-material-alter-code-modified="isMaterialAlterCodeModified"
              v-model:document-types="documentTypes"
              v-model:is-document-type-modified="isDocumentTypeModified"
              v-model:warehouses="warehouses"
              v-model:is-warehouse-modified="isWarehouseModified"
              v-model:customers="customers"
              v-model:is-customer-modified="isCustomerModified"
              v-model:representative-imports="representativeImports"
              v-model:is-representative-import-modified="isRepresentativeImportModified"
              v-model:trader-sites="traderSites"
              v-model:is-trader-site-modified="isTraderSiteModified"
              :item="representative"
              :representative="representative"
              :representative-id="String(route.params.id)"
              :representative-code="modifiedRepresentative?.code || '-'"
              :mode="mode"
              :validations="$v"
              :isHorizontalLayout="isHorizontalLayout"
              :reset-state="resetState" />
          </KeepAlive>
        </PerfectScrollbar>
      </div>

      <!-- Vertical Layout -->
      <div v-else class="h-full">
        <PerfectScrollbar class="w-full h-full py-4 space-y-2" ref="scrollbarId">
          <BaseAccordion
            v-for="tab in section1Tabs"
            :key="tab.name"
            :name="tab.label"
            :error="tab.validator && $v[tab.validator].required.$invalid">
            <RepresentativesDetailsTabProperties
              v-if="tab.name === 'properties'"
              v-model="modifiedRepresentative"
              :mode="mode"
              :validations="$v"
              :isHorizontalLayout="isHorizontalLayout" />
            <DetailsTabTrader
              v-else-if="tab.name === 'trader'"
              v-model="modifiedRepresentative"
              :item="representative"
              :mode="mode"
              :validations="$v"
              :isHorizontalLayout="isHorizontalLayout">
            </DetailsTabTrader>
            <CustomerDetailsTabTraderSite
              v-else-if="tab.name === 'traderSite'"
              v-model="modifiedRepresentative"
              :mode="mode"
              :isHorizontalLayout="isHorizontalLayout"
              :reset-state="resetState" />
            <RepresentativesDetailsTabDocumentTypes
              v-else-if="tab.name === 'documentTypes'"
              v-model:document-types="documentTypes"
              v-model:is-document-type-modified="isDocumentTypeModified"
              :mode="mode"
              :representative-id="route.params.id as string"
              :reset-state="resetState">
            </RepresentativesDetailsTabDocumentTypes>
            <RepresentativesDetailsTabWarehouses
              v-else-if="tab.name === 'warehouses'"
              v-model:warehouses="warehouses"
              v-model:is-warehouse-modified="isWarehouseModified"
              :mode="mode"
              :representative-id="route.params.id as string"
              :reset-state="resetState">
            </RepresentativesDetailsTabWarehouses>
            <RepresentativesDetailsTabCustomers
              v-else-if="tab.name === 'customers'"
              v-model:customers="customers"
              v-model:is-customer-modified="isCustomerModified"
              :mode="mode"
              :representative-id="route.params.id as string"
              :reset-state="resetState">
            </RepresentativesDetailsTabCustomers>
            <RepresentativesDetailsTabMaterialAlterCodes
              v-else-if="tab.name === 'materialAlterCodes'"
              v-model:material-alter-codes="materialAlterCodes"
              v-model:is-material-alter-code-modified="isMaterialAlterCodeModified"
              :mode="mode"
              :representative-id="route.params.id as string"
              :reset-state="resetState">
            </RepresentativesDetailsTabMaterialAlterCodes>
            <RepresentativesDetailsTabPaymentTypes
              v-else-if="tab.name === 'paymentTypes'"
              v-model:payment-types="paymentTypes"
              v-model:is-payment-type-modified="isPaymentTypeModified"
              :mode="mode"
              :representative-id="route.params.id as string"
              :reset-state="resetState">
            </RepresentativesDetailsTabPaymentTypes>
            <RepresentativesDetailsTabMeasurementUnits
              v-else-if="tab.name === 'measurementUnits'"
              v-model:measurement-units="measurementUnits"
              v-model:isMeasurementUnitModified="isMeasurementUnitModified"
              :mode="mode"
              :representative-id="route.params.id as string"
              :reset-state="resetState">
            </RepresentativesDetailsTabMeasurementUnits>
            <RepresentativesDetailsTabMapping
              v-else-if="tab.name === 'mapping'"
              :mode="mode"
              :representative-id="route.params.id as string"
              :representative-code="modifiedRepresentative?.code || '-'"
              :reset-state="resetState"
              v-model:representative-imports="representativeImports"
              v-model:is-representative-import-modified="isRepresentativeImportModified">
            </RepresentativesDetailsTabMapping>
            <RepresentativesDetailsTabTraderSites
              v-else-if="tab.name === 'traderSites'"
              v-model:trader-sites="traderSites"
              v-model:is-trader-site-modified="isTraderSiteModified"
              :mode="mode"
              :representative-id="route.params.id as string"
              :reset-state="resetState">
            </RepresentativesDetailsTabTraderSites>
          </BaseAccordion>
        </PerfectScrollbar>
      </div>
    </div>

    <BaseDrawerConfirm
      :is-active="confirmModalActive"
      @cancel="confirmModalActive = false"
      @confirm="onDiscardChanges" />
  </div>
</template>

<script setup lang="ts">
import { computed, markRaw, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import BaseAccordion from '@/components/common/BaseAccordion.vue';
import type { PerfectScrollbarExpose } from 'vue3-perfect-scrollbar';
import RepresentativesDetailsTabProperties from '@/components/traders/RepresentativesDetailsTabProperties.vue';
import DetailsTabTrader from '@/components/traders/DetailsTabTrader.vue';
import CustomerDetailsTabTraderSite from '@/components/traders/CustomerDetailsTabTraderSite.vue';
import RepresentativesDetailsTabMeasurementUnits from '@/components/traders/RepresentativesDetailsTabMeasurementUnits.vue';
import RepresentativesDetailsTabPaymentTypes from '@/components/traders/RepresentativesDetailsTabPaymentTypes.vue';
import RepresentativesDetailsTabMaterialAlterCodes from '@/components/traders/RepresentativesDetailsTabMaterialAlterCodes.vue';
import RepresentativesDetailsTabDocumentTypes from '@/components/traders/RepresentativesDetailsTabDocumentTypes.vue';
import RepresentativesDetailsTabWarehouses from '@/components/traders/RepresentativesDetailsTabWarehouses.vue';
import RepresentativesDetailsTabCustomers from '@/components/traders/RepresentativesDetailsTabCustomers.vue';
import RepresentativesDetailsTabMapping from '@/components/traders/RepresentativesDetailsTabMapping.vue';
import RepresentativesDetailsTabTraderSites from '@/components/traders/RepresentativesDetailsTabTraderSites.vue';
import type { Representative, Trader } from '@/types/Traders';
import { addOrEditRepresentative, getRepresentativeById } from '@/services/api/traders';
import { useI18n } from 'vue-i18n';
import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';
import useVuelidate from '@vuelidate/core';
import { useToastStore } from '@/stores/toast';
import { helpers, required, requiredIf } from '@vuelidate/validators';
import { deepCopy } from '@/utils/data';
import { v4 as uuidv4 } from 'uuid';
import {
  addOrEditDCMeasurementUnit,
  addOrEditDCPaymentType,
  addOrEditDCMaterialAlterCode,
  addOrEditDCDocumentType,
  addOrEditDCWarehouse,
  addOrEditDCCustomer,
  addOrEditDCRepresentativeImport,
  addOrEditDCTraderSite,
} from '@/services/api/dataCollection';
import { RecordMode, type SessionDetails } from '@/types/Base';
import type {
  DCCustomer,
  DCDocumentType,
  DCMaterialAlterCode,
  DCMeasurementUnit,
  DCPaymentType,
  DCWarehouse,
  DCRepresentativeImport,
  DCTraderSite,
} from '@/types/DataCollection';

const { getUsersLookup } = useLookupsStore();
const { users } = storeToRefs(useLookupsStore());
const { t: $t } = useI18n();
const representative = ref<Representative>();
const modifiedRepresentative = ref<Representative>();
const isHorizontalLayout = ref(true);
const scrollbarId = ref<PerfectScrollbarExpose>();
const mode = ref<'view' | 'edit'>('view');
const route = useRoute();
const router = useRouter();
const toastStore = useToastStore();
const isModified = ref<boolean>(false);
const confirmModalActive = ref(false);
const selectedTab = ref('properties');
const section1Tabs = ref([
  {
    name: 'properties',
    label: $t('customer.properties'),
    component: markRaw(RepresentativesDetailsTabProperties),
  },
  {
    name: 'trader',
    label: $t('customer.trader'),
    component: markRaw(DetailsTabTrader),
    validator: 'traderTaxNo',
  },
  {
    name: 'traderSite',
    label: $t('customer.defaultTraderSite'),
    component: markRaw(CustomerDetailsTabTraderSite),
  },
  {
    name: 'documentTypes',
    label: $t('representative.documentTypes'),
    component: markRaw(RepresentativesDetailsTabDocumentTypes),
  },
  {
    name: 'warehouses',
    label: $t('representative.warehouses'),
    component: markRaw(RepresentativesDetailsTabWarehouses),
  },
  {
    name: 'customers',
    label: $t('representative.customers'),
    component: markRaw(RepresentativesDetailsTabCustomers),
  },
  {
    name: 'traderSites',
    label: $t('representative.traderSites'),
    component: markRaw(RepresentativesDetailsTabTraderSites),
  },
  {
    name: 'materialAlterCodes',
    label: $t('representative.materialAlterCodes'),
    component: markRaw(RepresentativesDetailsTabMaterialAlterCodes),
  },
  {
    name: 'paymentTypes',
    label: $t('representative.paymentTypes'),
    component: markRaw(RepresentativesDetailsTabPaymentTypes),
  },
  {
    name: 'measurementUnits',
    label: $t('representative.measurementUnits'),
    component: markRaw(RepresentativesDetailsTabMeasurementUnits),
  },
  {
    name: 'mapping',
    label: $t('representative.mapping'),
    component: markRaw(RepresentativesDetailsTabMapping),
  },
]);
const currentComponent = computed(() => {
  const selected = section1Tabs.value.find((tab) => tab.name === selectedTab.value);
  return selected ? selected.component : null;
});

const isMeasurementUnitModified = ref(false);
const measurementUnits = ref<DCMeasurementUnit[]>([]);

const isPaymentTypeModified = ref(false);
const paymentTypes = ref<DCPaymentType[]>([]);

const isMaterialAlterCodeModified = ref(false);
const materialAlterCodes = ref<DCMaterialAlterCode[]>([]);

const isDocumentTypeModified = ref(false);
const documentTypes = ref<DCDocumentType[]>([]);

const isWarehouseModified = ref(false);
const warehouses = ref<DCWarehouse[]>([]);

const isCustomerModified = ref(false);
const customers = ref<DCCustomer[]>([]);

const isRepresentativeImportModified = ref(false);
const representativeImports = ref<DCRepresentativeImport[]>([]);

const isTraderSiteModified = ref(false);
const traderSites = ref<DCTraderSite[]>([]);

const hasModifiedRecords = computed(() => {
  const arrays = [
    measurementUnits.value,
    paymentTypes.value,
    materialAlterCodes.value,
    documentTypes.value,
    warehouses.value,
    customers.value,
    representativeImports.value,
    traderSites.value,
  ];

  return arrays.some(
    (array) =>
      array?.length &&
      array.some((item) => item.recordMode != null && item.recordMode !== RecordMode.Unchanged)
  );
});

watch(
  () => modifiedRepresentative.value,
  (newData) => {
    if (newData?.traderSites?.length && representative.value)
      representative.value.traderSites = newData.traderSites;
    isModified.value =
      JSON.stringify(newData) !== JSON.stringify(representative.value) || hasModifiedRecords.value;
    validationModel.value.traderName = newData?.trader?.name || '';
    validationModel.value.traderCode = newData?.trader?.code || '';
    validationModel.value.traderTaxNo = newData?.trader?.taxNumber || '';
    validationModel.value.dbName = newData?.dbName || '';
    validationModel.value.dbUsername = newData?.dbUsername || '';
    validationModel.value.dbPassword = newData?.dbPassword || '';
    validationModel.value.windowsAuthentication = newData?.windowsAuthentication || false;
  },
  { deep: true }
);

// Watch hasModifiedRecords to update isModified when arrays change
watch(
  () => hasModifiedRecords.value,
  (hasModified) => {
    isModified.value =
      JSON.stringify(modifiedRepresentative.value) !== JSON.stringify(representative.value) ||
      hasModified;
  }
);

const companyId = ref('');

onMounted(async () => {
  const session = localStorage.getItem('sessionDetails');
  if (session) {
    const sessionDetails = JSON.parse(session) as SessionDetails;
    companyId.value = sessionDetails.companyID;
  }
  await loadRepresentative();
  resetState.value = true;
});

async function loadRepresentative() {
  const res = await getRepresentativeById(route.params.id as string);
  if (res) {
    representative.value = res as Representative;
    await populateUsersFromLookups();
    mode.value = 'view';
  } else {
    const traderId = uuidv4();
    representative.value = {
      id: route.params.id as string,
      isFromManager: true,
      isActive: true,
      code: '',
      name: '',
      serverAddress: '',
      serverPort: '',
      erpName: '',
      companyId: companyId.value,
      traderId: traderId,
      dbName: '',
      dbUsername: '',
      dbPassword: '',
      trader: { id: traderId, name: '', code: '' } as Trader,
    };
    mode.value = 'edit';
  }
  modifiedRepresentative.value = deepCopy<Representative>(representative.value);
}

async function populateUsersFromLookups() {
  await getUsersLookup();
  if (representative.value?.createUserId)
    representative.value.createUser = users.value.find(
      (x) => x.id == representative.value?.createUserId
    );
  if (representative.value?.updateUserId)
    representative.value.updateUser = users.value.find(
      (x) => x.id == representative.value?.updateUserId
    );
}

const isBackMode = ref(false);
const resetState = ref(false);
function onCancelEdit(backMode: string) {
  if (isModified.value) {
    confirmModalActive.value = true;
    if (backMode == 'back') {
      isBackMode.value = true;
    }
    return;
  } else if (!representative.value?.createdDate) {
    router.push('/representatives');
  }
  mode.value = 'view';
  resetState.value = true;
}

function onDiscardChanges() {
  mode.value = 'view';
  confirmModalActive.value = false;
  if (representative.value)
    modifiedRepresentative.value = deepCopy<Representative>(representative.value);
  resetState.value = true;
  if (!representative.value?.createdDate || isBackMode.value) {
    router.push('/representatives');
  }
}

const validationModel = ref({
  traderName: modifiedRepresentative.value?.trader?.name || '',
  traderCode: modifiedRepresentative.value?.trader?.code || '',
  traderTaxNo: modifiedRepresentative.value?.trader?.taxNumber || '',
  dbName: modifiedRepresentative.value?.dbName || '',
  dbUsername: modifiedRepresentative.value?.dbUsername || '',
  dbPassword: modifiedRepresentative.value?.dbPassword || '',
  windowsAuthentication: modifiedRepresentative.value?.windowsAuthentication || false,
});

const rules = computed(() => ({
  traderName: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  traderCode: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  traderTaxNo: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  dbName: {},
  dbUsername: {
    requiredIf: helpers.withMessage(
      `${$t('messages.errors.required')}`,
      requiredIf(
        () => validationModel.value.windowsAuthentication !== true && !!validationModel.value.dbName
      )
    ),
  },
  dbPassword: {
    requiredIf: helpers.withMessage(
      `${$t('messages.errors.required')}`,
      requiredIf(
        () => validationModel.value.windowsAuthentication !== true && !!validationModel.value.dbName
      )
    ),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

async function onSaveChanges() {
  $v.value.$touch();
  if ($v.value.$invalid) {
    return;
  }
  mode.value = 'view';

  const hasTraderSiteChanges =
    modifiedRepresentative.value?.traderSites?.some(
      (ts) => ts.recordMode != null && ts.recordMode !== RecordMode.Unchanged
    ) || false;

  if (
    modifiedRepresentative.value &&
    (JSON.stringify(modifiedRepresentative.value) !== JSON.stringify(representative.value) ||
      hasTraderSiteChanges) &&
    (isModified.value || hasModifiedRecords.value || hasTraderSiteChanges)
  ) {
    const res = await addOrEditRepresentative(modifiedRepresentative.value);
    if (res) {
      toastStore.success({ text: `${$t('common.toast.success')}` });
      await loadRepresentative();
    } else {
      toastStore.error({ text: res });
    }
    isModified.value = false;
  }

  if (
    measurementUnits.value?.length &&
    measurementUnits.value.filter(
      (x) => x.recordMode != null && x.recordMode !== RecordMode.Unchanged
    ).length
  ) {
    const res = await addOrEditDCMeasurementUnit(
      measurementUnits.value.filter(
        (x) => x.recordMode != null && x.recordMode !== RecordMode.Unchanged
      )
    );
    if (res) {
      toastStore.success({ text: `${$t('common.toast.success')}` });
    } else {
      toastStore.error({ text: res });
    }
    isMeasurementUnitModified.value = false;
  }

  if (
    paymentTypes.value?.length &&
    paymentTypes.value.filter((x) => x.recordMode != null && x.recordMode !== RecordMode.Unchanged)
      .length
  ) {
    const res = await addOrEditDCPaymentType(
      paymentTypes.value.filter(
        (x) => x.recordMode != null && x.recordMode !== RecordMode.Unchanged
      )
    );
    if (res) {
      toastStore.success({ text: `${$t('common.toast.success')}` });
    } else {
      toastStore.error({ text: res });
    }
    isPaymentTypeModified.value = false;
  }

  if (
    materialAlterCodes.value?.length &&
    materialAlterCodes.value.filter(
      (x) => x.recordMode != null && x.recordMode !== RecordMode.Unchanged
    ).length
  ) {
    const res = await addOrEditDCMaterialAlterCode(
      materialAlterCodes.value.filter(
        (x) => x.recordMode != null && x.recordMode !== RecordMode.Unchanged
      )
    );
    if (res) {
      toastStore.success({ text: `${$t('common.toast.success')}` });
    } else {
      toastStore.error({ text: res });
    }
    isMaterialAlterCodeModified.value = false;
  }

  if (
    documentTypes.value?.length &&
    documentTypes.value.filter((x) => x.recordMode != null && x.recordMode !== RecordMode.Unchanged)
      .length
  ) {
    const res = await addOrEditDCDocumentType(
      documentTypes.value.filter(
        (x) => x.recordMode != null && x.recordMode !== RecordMode.Unchanged
      )
    );
    if (res) {
      toastStore.success({ text: `${$t('common.toast.success')}` });
    } else {
      toastStore.error({ text: res });
    }
    isDocumentTypeModified.value = false;
  }

  if (
    warehouses.value?.length &&
    warehouses.value.filter((x) => x.recordMode != null && x.recordMode !== RecordMode.Unchanged)
      .length
  ) {
    const res = await addOrEditDCWarehouse(
      warehouses.value.filter((x) => x.recordMode != null && x.recordMode !== RecordMode.Unchanged)
    );
    if (res) {
      toastStore.success({ text: `${$t('common.toast.success')}` });
    } else {
      toastStore.error({ text: res });
    }
    isWarehouseModified.value = false;
  }

  if (
    customers.value?.length &&
    customers.value.filter((x) => x.recordMode != null && x.recordMode !== RecordMode.Unchanged)
      .length
  ) {
    const res = await addOrEditDCCustomer(
      customers.value.filter((x) => x.recordMode != null && x.recordMode !== RecordMode.Unchanged)
    );
    if (res.isSuccess) toastStore.success({ text: `${$t('common.toast.success')}` });
    else
      toastStore.error({
        text: $t('messages.errors.' + res.error.code) || $t('common.toast.error'),
      });

    isCustomerModified.value = false;
  }

  if (
    representativeImports.value?.length &&
    representativeImports.value.filter(
      (x) => x.recordMode != null && x.recordMode !== RecordMode.Unchanged
    ).length
  ) {
    const res = await addOrEditDCRepresentativeImport(
      representativeImports.value.filter(
        (x) => x.recordMode != null && x.recordMode !== RecordMode.Unchanged
      )
    );
    if (res) {
      toastStore.success({ text: `${$t('common.toast.success')}` });
    } else {
      toastStore.error({ text: res });
    }
    isRepresentativeImportModified.value = false;
  }

  if (
    traderSites.value?.length &&
    traderSites.value.filter((x) => x.recordMode != null && x.recordMode !== RecordMode.Unchanged)
      .length
  ) {
    const res = await addOrEditDCTraderSite(
      traderSites.value.filter((x) => x.recordMode != null && x.recordMode !== RecordMode.Unchanged)
    );
    if (res) {
      toastStore.success({ text: `${$t('common.toast.success')}` });
    } else {
      toastStore.error({ text: res });
    }
    isTraderSiteModified.value = false;
  }

  resetState.value = true;
}

function onEditMode() {
  mode.value = 'edit';
  resetState.value = false;
}
</script>
